import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	DriveFileUpload,
	FileUploadStatus,
	UploadState,
	FolderPath,
	initialUploadState,
} from "../types/upload.types";

const uploadSlice = createSlice({
	name: "upload",
	initialState: initialUploadState,
	reducers: {
		// ====== S3 BUCKET INFO ======
		s3BucketInfoRequested: (state) => {
			state.s3BucketInfo.loading = true;
		},
		s3BucketInfoSuccess: (state, action: PayloadAction<string>) => {
			state.s3BucketInfo.loading = false;
			state.s3BucketInfo.encryptedBucketInfo = action.payload;
		},
		s3BucketInfoFailed: (state, action: PayloadAction<string>) => {
			state.s3BucketInfo.loading = false;
			state.s3BucketInfo.error = action.payload;
		},

		// ====== FILE READING ======
		readFiles: (state, action: PayloadAction<DriveFileUpload[]>) => {
			state.files = [...state.files, ...action.payload];
			state.totalFileSize =
				state.totalFileSize +
				action.payload.reduce((acc, file) => {
					return acc + file.size;
				}, 0);
			state.progress.Total += action.payload.length;
			state.progress.Queued += action.payload.reduce(
				(acc, item) => acc + (item.status === FileUploadStatus.Queued ? 1 : 0),
				0
			);

			// state.progress.UnSupportedFile += action.payload.reduce(
			// 	(acc, item) =>
			// 		acc + (item.status === FileUploadStatus.UnsupportedFileType ? 1 : 0),
			// 	0
			// );
		},
		clearFiles: (state) => {
			state.files = [];
		},
		removeSingleFile: (state, action: PayloadAction<DriveFileUpload>) => {
			const file = action.payload;

			// remove from list
			state.files = state.files.filter((f) => f.id !== file.id);

			// total size
			state.totalFileSize -= file.size;

			// total count
			if (state.progress.Total > 0) state.progress.Total -= 1;

			// adjust counters safely based on file status
			switch (file.status) {
				case FileUploadStatus.UnsupportedFileType:
					if (state.progress.UnSupportedFile > 0)
						state.progress.UnSupportedFile -= 1;
					break;

				case FileUploadStatus.Completed:
					if (state.progress.Completed > 0) state.progress.Completed -= 1;
					break;

				case FileUploadStatus.Failed:
					if (state.progress.Failed > 0) state.progress.Failed -= 1;
					break;

				case FileUploadStatus.Canceled:
					if (state.progress.Canceled > 0) state.progress.Canceled -= 1;
					break;

				case FileUploadStatus.Uploading:
					if (state.progress.Uploading > 0) state.progress.Uploading -= 1;
					break;

				default:
					if (state.progress.Queued > 0) state.progress.Queued -= 1;
			}
		},

		incrementSNo: (state) => {
			state.fileSnoCounter += 1;
		},
		// ====== UPLOAD ACTIONS ======

		initiateUpload: (state) => {
			state.inProgess = true;
		},

		documentUploadStart: (state, action: PayloadAction<string>) => {
			const file = state.files.find((f) => f.id === action.payload);
			if (file && file.status !== FileUploadStatus.Canceled) {
				file.status = FileUploadStatus.Uploading;
				state.progress.Queued -= 1;
				state.progress.Uploading += 1;
			}
		},

		documentUploadUpdate: (
			state,
			action: PayloadAction<{ id: string; changes: Partial<DriveFileUpload> }>
		) => {
			const { id, changes } = action.payload;
			const file = state.files.find((f) => f.id === id);
			if (!file) {
				return;
			}

			Object.assign(file, changes);

			if (changes.status) {
				switch (changes.status) {
					case FileUploadStatus.Completed:
						state.progress.Completed += 1;
						state.progress.Uploading -= 1;
						state.uploadedFileSize += file.size;
						break;
					case FileUploadStatus.Failed:
						state.progress.Failed += 1;
						break;
					case FileUploadStatus.Canceled:
						state.progress.Canceled += 1;
						state.cancelledList.push(id);
						break;
					case FileUploadStatus.UnsupportedFileType:
						state.progress.UnSupportedFile += 1;
						state.progress.Uploading -= 1;
						break;
				}
			}
		},

		documentUploadProgress: (
			state,
			action: PayloadAction<{ id: string; progress: number }>
		) => {
			const file = state.files.find((f) => f.id === action.payload.id);
			if (file && file.progress !== 100) {
				file.progress = action.payload.progress;
				const totalProgress =
					state.files.reduce((acc, f) => acc + (f.progress || 0), 0) /
					(state.files.length || 1);
				state.progress.TotalProgPercent = Math.floor(totalProgress);
			}
		},

		documentUploadError: (state, action: PayloadAction<string>) => {
			state.error = action.payload;
		},

		// ====== VIEW / PAGINATION ======
		toggleExpendView: (state, action: PayloadAction<boolean>) => {
			state.isExpendView = action.payload;
		},
		changePageNumber: (state, action: PayloadAction<number>) => {
			state.pagination.page = action.payload;
		},
		changePageSize: (state, action: PayloadAction<number>) => {
			state.pagination.size = action.payload;
			state.pagination.page = 0;
		},

		// ====== FOLDER PATH MANAGEMENT ======
		addFolderPath: (state, action: PayloadAction<FolderPath>) => {
			state.folderCreatedPaths[action.payload.pathHash] = action.payload;
		},

		setS3FolderName: (state, action: PayloadAction<string>) => {
			state.s3FolderName = action.payload;
		},

		// ====== RESET ======
		clearUploadState: () => initialUploadState,
		changeUploadOnGoingState: (state, action: PayloadAction<boolean>) => {
			state.inProgess = action.payload;
		},
	},
});

export const {
	s3BucketInfoRequested,
	s3BucketInfoSuccess,
	s3BucketInfoFailed,
	readFiles,
	clearFiles,
	removeSingleFile,
	initiateUpload,
	documentUploadStart,
	documentUploadUpdate,
	documentUploadProgress,
	documentUploadError,
	toggleExpendView,
	changePageNumber,
	changePageSize,
	addFolderPath,
	clearUploadState,
	incrementSNo,
	setS3FolderName,
	changeUploadOnGoingState,
} = uploadSlice.actions;

export default uploadSlice.reducer;
